/**
 * @fileoverview Audit Log page.
 * @description A Super Admin's read-only record of who did what in the system and when, from the
 * backend's Search Audit Events API. Defaults to the last seven days of platform-level events (no
 * tenant selected); filters narrow by date range (required by the API, at most a year), result,
 * actor type, source module and exact event type, results are paged twenty at a time, and a row
 * opens the event's full detail. Names, emails and IPs arrive masked from the backend. Searches are
 * themselves audited and rate-limited by the backend, so nothing here refetches on its own. The
 * route is gated to Super Admin (see AppRoutes and capability-access.utils); this page assumes it.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  AUDIT_ACTOR_TYPES,
  AUDIT_MAX_RESULT_WINDOW,
  AUDIT_PAGE_SIZE,
  AUDIT_RESULTS,
  AUDIT_SOURCE_MODULES,
  AuditEventDetailModal,
  AuditEventRow,
  AuditTableSkeleton,
  AUDIT_TABLE_GRID_CLASS,
  AUDIT_TABLE_MIN_WIDTH_CLASS,
  checkAuditRange,
  defaultAuditFilters,
  isValidEventTypeFilter,
  toAuditParams,
  useAuditEvents,
} from '@/features/audit'
import Badge from '@/shared/components/atoms/Badge'
import Select from '@/shared/components/atoms/Select'
import PageHeader from '@/shared/components/molecules/PageHeader'
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue'

import type { AuditActorType, AuditFilters, AuditResult } from '@/features/audit'

const COLUMN_KEYS = ['when', 'who', 'event', 'resource', 'module', 'result'] as const
const FIELD_CLASS =
  'h-[38px] rounded-[8px] border border-input-border bg-white px-3 text-sm font-medium text-ink focus:outline-none focus:ring-2 focus:ring-primary/20'
const PAGE_BUTTON_CLASS =
  'cursor-pointer rounded-lg border border-input-border bg-white px-3 py-1.5 text-[13px] font-semibold text-ink hover:border-border-hover disabled:cursor-not-allowed disabled:opacity-50'

/**
 * @component
 */
function AuditLogPage() {
  const { t, i18n } = useTranslation()
  const locale = i18n.resolvedLanguage ?? 'en'

  const [defaults] = useState(() => defaultAuditFilters())
  const [filters, setFilters] = useState<AuditFilters>(defaults)
  const [page, setPage] = useState(1)
  const [selectedEventId, setSelectedEventId] = useState('')

  // The event type is typed freely, so it is sent only once typing pauses. Emptying the box applies
  // at once, since there is nothing left to wait for.
  const debouncedEventType = useDebouncedValue(filters.eventType)
  const appliedEventType = filters.eventType === '' ? '' : debouncedEventType
  // The old page number means nothing for a different result set, so the page returns to 1 when the
  // event type actually sent changes (not on each keystroke, which would send a page-1 request with
  // the old event type first). Adjusted during render so no request goes out with the stale page.
  const [seenEventType, setSeenEventType] = useState(appliedEventType)
  if (seenEventType !== appliedEventType) {
    setSeenEventType(appliedEventType)
    setPage(1)
  }
  const rangeProblem = checkAuditRange(filters.fromDate, filters.toDate)
  const eventTypeInvalid = !isValidEventTypeFilter(filters.eventType)

  const params = useMemo(
    () => toAuditParams({ ...filters, eventType: appliedEventType }, page, AUDIT_PAGE_SIZE),
    [filters, appliedEventType, page],
  )
  const eventsQuery = useAuditEvents(params)

  const events = eventsQuery.data?.items ?? []
  const total = eventsQuery.data?.meta.total ?? 0
  const searchScope = eventsQuery.data?.meta.searchScope
  const reachableTotal = Math.min(total, AUDIT_MAX_RESULT_WINDOW)
  const pageCount = Math.max(1, Math.ceil(reachableTotal / AUDIT_PAGE_SIZE))
  // The rows on screen belong to the page the response says, which lags `page` while the next one
  // loads (the previous rows stay up meanwhile), so the footer must follow the response.
  const shownPage = eventsQuery.data?.meta.page ?? page
  const firstShown = events.length === 0 ? 0 : (shownPage - 1) * AUDIT_PAGE_SIZE + 1
  const lastShown = events.length === 0 ? 0 : firstShown + events.length - 1
  const isFiltered = (Object.keys(defaults) as (keyof AuditFilters)[]).some(
    (key) => filters[key] !== defaults[key],
  )

  /**
   * @description Changes some filters and returns to the first page, since the old page number
   * means nothing for a different result set.
   * @param {Partial<AuditFilters>} patch - The filters that changed.
   */
  const updateFilters = (patch: Partial<AuditFilters>) => {
    setFilters((current) => ({ ...current, ...patch }))
    setPage(1)
  }

  /**
   * @description Puts every filter back to its default (the last seven days, nothing else set).
   */
  const clearFilters = () => {
    setFilters(defaults)
    setPage(1)
  }

  /**
   * @description Closes the detail dialog.
   */
  const closeDetail = () => {
    setSelectedEventId('')
  }

  // No scope in the response means it is unknown, so no badge rather than a guessed one.
  let scopeLabel = ''
  if (searchScope?.type === 'TENANT') {
    scopeLabel = t('audit.scope.TENANT', { tenantId: searchScope.tenantId })
  } else if (searchScope) {
    scopeLabel = t(`audit.scope.${searchScope.type}`)
  }

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={t('nav.auditLog')} description={t('audit.description')} />

      <section className="rounded-[10px] border border-divider bg-white">
        <div className="flex flex-wrap items-center gap-2.5 border-b border-divider px-4 py-3 sm:px-5">
          <b className="text-[14px] text-ink" data-testid="audit-log-section-title">
            {t('audit.activity')}
          </b>
          {scopeLabel && (
            <Badge variant="info" data-testid="audit-log-scope-badge">
              {scopeLabel}
            </Badge>
          )}
          <span className="flex-1" />
          {eventsQuery.data && (
            <span className="text-xs text-slate" data-testid="audit-log-total-events">
              {t('audit.totalEvents', { total })}
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-end gap-2.5 p-3.5 sm:p-4">
          <label className="flex flex-col gap-1 text-[11px] font-semibold tracking-[0.5px] text-table-header">
            {t('audit.filters.from')}
            <input
              type="date"
              value={filters.fromDate}
              onChange={(event) => updateFilters({ fromDate: event.target.value })}
              data-testid="audit-log-from-date-input"
              className={FIELD_CLASS}
            />
          </label>
          <label className="flex flex-col gap-1 text-[11px] font-semibold tracking-[0.5px] text-table-header">
            {t('audit.filters.to')}
            <input
              type="date"
              value={filters.toDate}
              onChange={(event) => updateFilters({ toDate: event.target.value })}
              data-testid="audit-log-to-date-input"
              className={FIELD_CLASS}
            />
          </label>
          <Select
            aria-label={t('audit.filters.result')}
            data-testid="audit-log-result-select"
            value={filters.result}
            onChange={(event) => updateFilters({ result: event.target.value as AuditResult | '' })}
            options={[
              { value: '', label: t('audit.filters.allResults') },
              ...AUDIT_RESULTS.map((value) => ({ value, label: t(`audit.result.${value}`) })),
            ]}
          />
          <Select
            aria-label={t('audit.filters.actorType')}
            data-testid="audit-log-actor-type-select"
            value={filters.actorType}
            onChange={(event) =>
              updateFilters({ actorType: event.target.value as AuditActorType | '' })
            }
            options={[
              { value: '', label: t('audit.filters.allActorTypes') },
              ...AUDIT_ACTOR_TYPES.map((value) => ({
                value,
                label: t(`audit.actorType.${value}`),
              })),
            ]}
          />
          <Select
            aria-label={t('audit.filters.module')}
            data-testid="audit-log-module-select"
            value={filters.sourceModule}
            onChange={(event) => updateFilters({ sourceModule: event.target.value })}
            options={[
              { value: '', label: t('audit.filters.allModules') },
              ...AUDIT_SOURCE_MODULES.map((value) => ({ value, label: value })),
            ]}
          />
          <input
            type="text"
            aria-label={t('audit.filters.eventType')}
            aria-invalid={eventTypeInvalid}
            data-testid="audit-log-event-type-input"
            value={filters.eventType}
            onChange={(event) =>
              setFilters((current) => ({ ...current, eventType: event.target.value }))
            }
            placeholder={t('audit.filters.eventTypePlaceholder')}
            className={`${FIELD_CLASS} min-w-60 flex-1 placeholder:font-normal placeholder:text-placeholder`}
          />
          {/* Always mounted so toggling filters never flips this row between one/two lines. */}
          <button
            type="button"
            onClick={clearFilters}
            disabled={!isFiltered}
            aria-hidden={!isFiltered}
            data-testid="audit-log-clear-filters-button"
            className={`rounded-lg border border-input-border bg-white px-3.5 py-2 text-[13px] font-semibold text-slate ${
              isFiltered ? 'cursor-pointer hover:border-border-hover hover:text-ink' : 'invisible'
            }`}
          >
            {t('audit.filters.clear')}
          </button>
        </div>

        {(rangeProblem || eventTypeInvalid) && (
          <p
            role="alert"
            className="px-4 pb-3 text-xs text-danger sm:px-5"
            data-testid="audit-log-filter-error"
          >
            {rangeProblem ? t(`audit.range.${rangeProblem}`) : t('audit.filters.eventTypeInvalid')}
          </p>
        )}

        <div className="overflow-x-auto border-t border-divider" data-testid="audit-log-table">
          <div className={AUDIT_TABLE_MIN_WIDTH_CLASS}>
            <div
              className={`${AUDIT_TABLE_GRID_CLASS} items-center gap-3 bg-input-bg px-4 py-2.5 sm:px-5`}
              data-testid="audit-log-table-header"
            >
              {COLUMN_KEYS.map((key) => (
                <span
                  key={key}
                  className="text-[11px] font-semibold tracking-[0.6px] text-table-header"
                >
                  {t(`audit.columns.${key}`)}
                </span>
              ))}
            </div>

            {params === null && (
              <p
                className="px-5 py-10 text-center text-sm text-slate"
                data-testid="audit-log-choose-valid-range"
              >
                {t('audit.range.chooseValid')}
              </p>
            )}

            {params !== null && eventsQuery.isPending && <AuditTableSkeleton />}

            {params !== null && eventsQuery.isError && (
              <div
                role="alert"
                className="flex flex-col items-center gap-2 px-5 py-10 text-center"
                data-testid="audit-log-load-error"
              >
                <p className="text-sm text-danger" data-testid="audit-log-load-error-message">
                  {eventsQuery.error.message || t('audit.loadError')}
                </p>
                <button
                  type="button"
                  onClick={() => void eventsQuery.refetch()}
                  data-testid="audit-log-retry-button"
                  className="cursor-pointer text-[13px] font-semibold text-primary underline"
                >
                  {t('audit.retry')}
                </button>
              </div>
            )}

            {params !== null && eventsQuery.data && events.length === 0 && (
              <div
                className="flex flex-col items-center gap-1 px-5 py-10 text-center"
                data-testid="audit-log-empty-state"
              >
                <p
                  className="text-sm font-semibold text-ink"
                  data-testid="audit-log-empty-state-title"
                >
                  {t('audit.empty.title')}
                </p>
                <p className="text-[13px] text-slate" data-testid="audit-log-empty-state-hint">
                  {t('audit.empty.hint')}
                </p>
              </div>
            )}

            {params !== null && events.length > 0 && (
              <div
                aria-busy={eventsQuery.isFetching}
                data-testid="audit-log-rows"
                className={eventsQuery.isFetching ? 'opacity-60 transition-opacity' : undefined}
              >
                {events.map((event) => (
                  <AuditEventRow
                    key={event.auditEventId}
                    event={event}
                    locale={locale}
                    onSelect={setSelectedEventId}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {events.length > 0 && (
          <div
            className="flex flex-wrap items-center gap-3 border-t border-divider bg-surface-hover px-4 py-2.5 sm:px-5"
            data-testid="audit-log-footer"
          >
            <span className="flex-1 text-xs text-slate" data-testid="audit-log-showing">
              {t('audit.footer.showing', { from: firstShown, to: lastShown, total })}
              {total > AUDIT_MAX_RESULT_WINDOW && (
                <span
                  className="mt-0.5 block text-placeholder"
                  data-testid="audit-log-window-limit"
                >
                  {t('audit.footer.windowLimit', { max: AUDIT_MAX_RESULT_WINDOW })}
                </span>
              )}
            </span>
            <span
              className="text-xs font-medium text-placeholder"
              data-testid="audit-log-page-line"
            >
              {t('audit.footer.page', { page: shownPage, pages: pageCount })}
            </span>
            <button
              type="button"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={page <= 1 || eventsQuery.isFetching}
              data-testid="audit-log-previous-button"
              className={PAGE_BUTTON_CLASS}
            >
              {t('audit.footer.previous')}
            </button>
            <button
              type="button"
              onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
              disabled={page >= pageCount || eventsQuery.isFetching}
              data-testid="audit-log-next-button"
              className={PAGE_BUTTON_CLASS}
            >
              {t('audit.footer.next')}
            </button>
          </div>
        )}
      </section>

      <AuditEventDetailModal auditEventId={selectedEventId} onClose={closeDetail} />
    </div>
  )
}

export default AuditLogPage
